/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { CompanyDTO, CompanyFilterDTO, OpeningDTO, OpeningFilterDTO, ProductDTO, ProductFilterDTO, UserDTO, UserFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IReportProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const Report: React.FC<IReportProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [salesPerscpectiveList, setSalesPerspectiveList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [openingDTO, setOpeningDTO] = useState<OpeningDTO>();
    const [productList, setProductList] = useState<ProductDTO[]>([]);

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setOpeningDTO({
            ...openingDTO,
            id: '',
            code: '',
            laboratory: '',
            salePerspectiveId: '',
            description: '',
            information: '',
            developmentDeadline: '',
            companyId: ''
        } as OpeningDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.corpsphere.opening.salesPerspective, setSalesPerspectiveList);
            handleCompanyGetAll();
            handleUserGetAllAsync(setUserList);
            handleGetOpeningByIdAsync();
            handleProductGetAllViewAsync();
        }
    }, [data, isOpenMaintenance]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetOpeningByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('corpsphere.opening', data.id, setLoading, setOpeningDTO, showMessage);
    }, [data]);

    const handleProductGetAllViewAsync = useCallback(async () => {
        const productFilterDTO = {
            openingId: data.id
        };
    
        GenericService.GetAllViewAsync1('corpsphere.product', productFilterDTO, setActionExecuted, setLoading, setProductList, showMessage, true);
    }, [data]);
    
    const handleSaveClick = useCallback(async () => {
        window.print();
    }, [openingDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'print', text: 'Print', icon: 'print_outlined', iconColor: 'primary', setAction: handleSaveClick, isClose: false },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];
    
    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='SDT - REPORT'
                icon='summarize_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'code'}
                                label='Code'
                                value={openingDTO?.code}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'laboratory'}
                                label='Laboratory'
                                value={openingDTO?.laboratory}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'salePerspectiveId'}
                                label='Sale Perspective'
                                value={String(salesPerscpectiveList.find(x => x.id === openingDTO?.salePerspectiveId)?.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldReadOnly
                                id={'developmentDeadline'}
                                label='Develop Deadline'
                                value={openingDTO?.developmentDeadline}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextFieldReadOnly
                                id={'salePerspectiveId'}
                                label='Sale Perspective'
                                value={String(companyList.find(x => x.id === openingDTO?.companyId)?.fullName)}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'description'}
                                label='Description'
                                value={openingDTO?.description}
                                noWrap={true}
                                hasDivider={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldReadOnly
                                id={'information'}
                                label='Additional Information'
                                value={openingDTO?.information}
                                noWrap={true}
                            />
                        </Grid>
                        {productList ? productList.map((product, index) => (
                            <React.Fragment key={index}>
                                <Grid item xs={12} md={12}>
                                    <Typography sx={{ fontSize: 12 }} color="text.secondary" gutterBottom>
                                        Item {index}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={12} mb={-1}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'name'}
                                        label='Name'
                                        value={product.name}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'code'}
                                        label='Code'
                                        value={product.code}
                                    />
                                </Grid>
                            </React.Fragment>
                        )) : 
                            <React.Fragment>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={2}>
                                        <TextFieldReadOnly
                                            id={'product'}
                                            label='Product not found'
                                            value={'No Product'}
                                        />
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        }

                        {openingDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={openingDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === openingDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(openingDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === openingDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(openingDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};